import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, BookOpen, Save, Printer, Target } from 'lucide-react';
import { toast } from 'sonner';
import AIPracticeScenario from '../shared/AIPracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function CompassionateWheel() {
  const navigate = useNavigate();
  const [wheelData, setWheelData] = useState(() => {
    const saved = localStorage.getItem('compassionateWheel');
    return saved ? JSON.parse(saved) : { need: '', feelingsMet: '', feelingsUnmet: '', strategies: '' };
  });

  const printPage = () => { window.print(); toast.success('Print dialog opened'); };
  
  const saveProgress = () => {
    localStorage.setItem('compassionateWheel', JSON.stringify(wheelData));
    
    // Also save to user dashboard
    const userId = localStorage.getItem('workbook_user_id');
    if (userId) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/save-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          page_number: 25,
          page_title: 'Compassionate Communication Wheel',
          response_data: wheelData,
          timestamp: new Date().toISOString()
        })
      });
    }
    
    toast.success('Saved to your dashboard!');
  };

  useEffect(() => {
    const interval = setInterval(() => {
      localStorage.setItem('compassionateWheel', JSON.stringify(wheelData));
    }, 30000);
    return () => clearInterval(interval);
  }, [wheelData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Compassionate Communication Wheel</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <Button onClick={saveProgress} variant="outline" size="sm" className="border-green-600 hover:bg-green-900/20 text-white">
                <Save className="h-4 w-4 mr-2" />Save
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 25</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <Target className="h-16 w-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">Compassionate Communication Wheel</h2>
            <p className="text-slate-300 leading-relaxed">
              A tool for identifying needs, feelings, and strategies in conflict or celebration
            </p>
          </div>

          {/* Interactive Wheel - Full Version */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-8 mb-8">
            <div className="space-y-6">
              {/* Center: Need */}
              <div className="text-center">
                <label className="text-green-300 font-bold text-lg mb-3 block">NEED (Center of the Wheel)</label>
                <Textarea 
                  value={wheelData.need}
                  onChange={(e) => setWheelData({...wheelData, need: e.target.value})}
                  placeholder="What need is alive for you? (e.g., connection, understanding, autonomy...)"
                  className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 min-h-[80px] text-center text-lg"
                />
              </div>

              {/* Two Columns: When Met vs When Not Met */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* When Needs Met */}
                <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-green-200 mb-4 text-center">😊 When Needs ARE Met</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-green-300 text-sm mb-2 block">Feelings:</label>
                      <Textarea 
                        value={wheelData.feelingsMet}
                        onChange={(e) => setWheelData({...wheelData, feelingsMet: e.target.value})}
                        placeholder="How do you feel when this need IS met?"
                        className="bg-green-900/30 border-green-700/40 text-white placeholder:text-green-300/40 min-h-[80px]"
                      />
                    </div>
                  </div>
                </div>

                {/* When Needs NOT Met */}
                <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-red-200 mb-4 text-center">😔 When Needs are NOT Met</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-red-300 text-sm mb-2 block">Feelings:</label>
                      <Textarea 
                        value={wheelData.feelingsUnmet}
                        onChange={(e) => setWheelData({...wheelData, feelingsUnmet: e.target.value})}
                        placeholder="How do you feel when this need is NOT met?"
                        className="bg-red-900/30 border-red-700/40 text-white placeholder:text-red-300/40 min-h-[80px]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Strategies / Requests */}
              <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-6">
                <h3 className="text-xl font-bold text-amber-200 mb-4 text-center">Strategies / Requests to Meet This Need</h3>
                <Textarea 
                  value={wheelData.strategies}
                  onChange={(e) => setWheelData({...wheelData, strategies: e.target.value})}
                  placeholder="What specific, doable requests or strategies could help meet this need?"
                  className="bg-amber-900/30 border-amber-700/40 text-white placeholder:text-amber-300/40 min-h-[100px]"
                />
              </div>
            </div>
          </div>

          <p className="text-center text-slate-400 text-sm mb-8">💾 Auto-saves every 30 seconds</p>

          {/* AI Practice Scenario */}
          <AIPracticeScenario pageTitle="Compassionate Communication Wheel" pageNumber={25} />

          {/* Page Messaging */}
          <PageMessaging pageTitle="Compassionate Communication Wheel" pageNumber={25} />
        </div>
      </div>
    </div>
  );
}
